import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { imageHelper } from 'common/functions/image-helper';
import { ListingImageUpdate_Upload, useListingsUploadImagesMutation } from 'graphql/generated';
import _ from 'lodash';
import { customAlphabet } from 'nanoid';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cacheActions } from 'redux/cache.slice';
import { newListingActions, newListingSelectors } from 'redux/new-listing.slice';
import { RootState } from 'redux/root-reducer';
import WsFlexbox from 'stories/layout/WsFlexbox';
import WsGridContainer from 'stories/layout/WsGridContainer';

import { StepProps } from './CreateListing';
import ImageAdder from './ImageAdder';
import StepperFooter from './StepperFooter';

const nanoid = customAlphabet('1234567890abcdefghijklmnopqrstuvwxyz', 12);
const MAX_IMAGES = 12;

const ListingPhotos = (props: StepProps) => {
  const dispatch = useDispatch();

  const [uploadImages] = useListingsUploadImagesMutation();

  const [newImageTotal, newImageTotalTo] = React.useState(0);
  const [busyStatus, busyStatusTo] = React.useState(false);

  const images = useSelector((state: RootState) => newListingSelectors.images(state));
  const primaryImageIdx = images.findIndex(x => x.primary);

  useEffect(() => {
    if (newImageTotal > 0 && newImageTotal === images.length) {
      newImageTotalTo(0);
      busyStatusTo(false);
    }
  }, [newImageTotal, images, props]);

  const onDeletePhotoIntent = (idx: number) => {
    dispatch(newListingActions.imageRemove(idx));
  };

  const blobToBase64 = (blob: Blob) => {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = function () {
        resolve(reader.result);
      };
    });
  };

  const addImageFn = async (blobs: Blob[]) => {
    newImageTotalTo(images.length + blobs.length);
    const uploads = await Promise.all(
      blobs.map(
        async x =>
          ({
            id: nanoid(),
            content: JSON.stringify({ data: await blobToBase64(x) }),
          } as ListingImageUpdate_Upload),
      ),
    );

    try {
      const { data } = await uploadImages({
        variables: { uploads },
      });
      if (!data) throw new Error('add image fatal error');
      const results = data.listingsUploadImages;
      for (const result of results) {
        dispatch(newListingActions.imageAdd(result));
      }
    } catch (error: any) {
      dispatch(
        cacheActions.addSnack({
          message: error.message,
          variant: 'error',
        }),
      );
    }
  };

  const onPrimaryChange = (idx: number) => dispatch(newListingActions.imageSetPrimary(idx));
  const onImageOrderDown = (idx: number) => dispatch(newListingActions.imageOrderDown(idx));
  const onImageOrderUp = (idx: number) => dispatch(newListingActions.imageOrderUp(idx));

  const pictureFrame = (idx: number, hideOrderButtons?: boolean) => {
    return (
      <WsFlexbox
        centered
        direction="column"
        sx={{
          border: '1px solid #CCC',
        }}
      >
        <WsFlexbox padding={0} sx={{ width: '100%', paddingLeft: 1, paddingRight: 1, justifyContent: 'space-between' }}>
          <Tooltip title="Make Primary">
            <IconButton
              color="primary"
              sx={{ padding: 0 }}
              aria-label="delete"
              component="span"
              disabled={images[idx].primary}
              onClick={() => onPrimaryChange(idx)}
            >
              <PhotoCameraIcon />
            </IconButton>
          </Tooltip>
          {!images[idx].primary ? (
            <div>
              <Tooltip title="Move Left">
                <IconButton
                  color="primary"
                  sx={{ padding: 0 }}
                  aria-label="delete"
                  component="span"
                  disabled={idx < 2}
                  onClick={() => onImageOrderDown(idx)}
                >
                  <ArrowLeftIcon fontSize="large" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Move Right">
                <IconButton
                  color="primary"
                  sx={{ padding: 0 }}
                  aria-label="delete"
                  component="span"
                  disabled={idx > images.length - 2}
                  onClick={() => onImageOrderUp(idx)}
                >
                  <ArrowRightIcon fontSize="large" />
                </IconButton>
              </Tooltip>
            </div>
          ) : (
            <Typography variant="h6">Primary Photo</Typography>
          )}
          <IconButton
            color="warning"
            aria-label="delete"
            sx={{ padding: 0 }}
            component="span"
            onClick={() => onDeletePhotoIntent(idx)}
          >
            <DeleteForeverIcon />
          </IconButton>
        </WsFlexbox>
        <WsFlexbox
          sx={{
            padding: 0,
          }}
          centered
        >
          <img src={imageHelper.getLocation(images[idx])} alt="car" width="100%" />
        </WsFlexbox>
      </WsFlexbox>
    );
  };

  const setBusy = (busy: boolean) => {
    busyStatusTo(busy);
  };

  return (
    <WsGridContainer>
      <Grid
        container
        item
        sx={{
          width: '100%',
          paddingTop: 1.5,
          paddingBottom: 0.5,
        }}
      >
        {primaryImageIdx >= 0 && (
          <Grid container item xs={12}>
            {/* <Grid item xs={4} /> */}
            <Grid key={`photo-grid${primaryImageIdx.toString()}`} sx={{ justifyContent: 'center' }} item xs={3}>
              {pictureFrame(primaryImageIdx)}
            </Grid>
            <Grid item xs={4} />
          </Grid>
        )}
      </Grid>
      <Grid
        container
        item
        sx={{
          width: '100%',
          paddingTop: 1.5,
          paddingBottom: 1.5,
        }}
      >
        {images.map(
          (x, idx) =>
            idx !== primaryImageIdx && (
              <Grid key={`photo-grid${idx.toString()}`} item xs={6} sm={2}>
                {pictureFrame(idx)}
              </Grid>
            ),
        )}
      </Grid>
      <Grid
        container
        item
        justifyContent="center"
        sx={{
          paddingTop: 1.5,
          paddingBottom: 1.5,
        }}
      >
        <ImageAdder
          disabled={images.length >= MAX_IMAGES || busyStatus}
          imageCount={images.length}
          maxImages={MAX_IMAGES}
          addImageFn={addImageFn}
          setBusyFn={setBusy}
          {...props}
        />
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', paddingTop: 1 }}>
          <Typography variant="body1">A minimim of 2 photos are required, with a maximum of 12.</Typography>
        </Grid>
      </Grid>
      <StepperFooter
        handleCancel={props.handleCancel}
        isFirst={props.isFirst}
        isLast={props.isLast}
        allDisabled={busyStatus}
        nextDisabled={images.length < 2}
        handleBack={props.handleBack}
        handleNext={props.handleNext}
      />
    </WsGridContainer>
  );
};

export default ListingPhotos;
